import React, {useEffect, useRef, useState} from 'react';
import {InputLabel, Stack, TextField} from '@mui/material';
import {unstable_useEnhancedEffect as useEnhancedEffect} from '@mui/utils';

interface NumberTextFieldComponentProps {
  color?: 'primary' | 'secondary' | 'error';
  defaultIfEmpty?: number;
  label?: string;
  labelWidth?: number;
  handleChange?: (value?: number) => void;
  handleBlur?: (value?: number) => void;
  inputWidth?: number;
  required?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  pattern?: RegExp;
  fullWidth?: boolean;
  disabled?: boolean;
  gateStatus?: string;
  size?: 'small';
  value?: number;
  error?: string;
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  preventClick?: boolean;
  inputColor?: string;
  centered?: boolean;
  hasFocus?: boolean;
}

const NumberTextFieldComponent = (props: NumberTextFieldComponentProps) => {
  const {
    centered,
    color = 'primary',
    defaultIfEmpty,
    startAdornment,
    endAdornment,
    error,
    fullWidth,
    gateStatus,
    handleChange,
    handleBlur,
    inputWidth,
    label,
    labelWidth,
    placeholder,
    readOnly,
    size,
    disabled,
    pattern,
    required,
    value,
    preventClick = false,
    inputColor,
    hasFocus,
  } = props;

  const [inputValue, setInputValue] = useState(value || '');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value === 0) {
      setInputValue('0');
      return;
    }
    if (inputValue !== value?.toString()) setInputValue(value != null ? value.toString() : '');
  }, [value]);

  useEnhancedEffect(() => {
    if (hasFocus && ref.current && !readOnly) {
      let input;
      if (value) {
        input = ref.current.querySelector<HTMLInputElement>(
          `input[value="${value}"]`,
        );
      } else {
        input = ref.current.querySelector<HTMLInputElement>(
          'input[placeholder="-"]',
        );
      }

      input?.focus();
    }
  }, [hasFocus, value, readOnly]);

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      {!!label && (
        <InputLabel error={!!error} sx={{width: labelWidth ? `${labelWidth}px` : undefined}}>
          {label + (required ? '*' : '')}
        </InputLabel>
      )}
      <TextField
        ref={ref}
        sx={{
          flex: !inputWidth ? 1 : undefined,
          width: inputWidth ? `${inputWidth}px` : undefined,
          input: {color: inputColor, textAlign: centered ? 'center' : undefined},

        }}
        color={color}
        size={size}
        style={{color: 'red !important'}}
        aria-label={gateStatus !== undefined ? 'mandatory-gate' : undefined}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
        placeholder={!readOnly ? placeholder : undefined}
        value={inputValue}
        onClick={(evt) => preventClick ? evt.stopPropagation() : null}
        onChange={(evt) => {
          const value = evt.target.value.replace(',', '.').slice(0, 15);
          evt.target.value = value;

          if (pattern && value && !pattern.test(value.toString())) {
            evt.target.value = value.slice(0, value.length - 1);
            return;
          }

          setInputValue(value);
          if (!value) return;

          const floatValue = parseFloat(value);
          if (!!value && !isNaN(floatValue)) {
            handleChange?.(floatValue);
          }
        }}
        onBlur={!readOnly ? () => {
          const floatValue = parseFloat(inputValue?.toString());
          if (floatValue != null && !isNaN(floatValue)) {
            handleBlur?.(floatValue);
          }
          if (!inputValue) {
            if (defaultIfEmpty) setInputValue(defaultIfEmpty.toString());
            return handleChange?.(defaultIfEmpty);
          }
        } : undefined}
        focused={readOnly ? false : undefined}
        InputProps={{
          readOnly,
          startAdornment,
          endAdornment,
        }}
        inputProps={{pattern}}
        error={!!error}
        helperText={error}/>
    </Stack>
  );
};

export default NumberTextFieldComponent;
